// StripeInlinePayment.tsx  — FIX: không remount liên tục
import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { CreditCard, CheckCircle, AlertCircle, ShieldCheck } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useApiWithRecaptcha } from '@/utils/apiWithRecaptcha';
import { useRecaptcha } from '@/contexts/RecaptchaContext';
import { getStripePublishableKey } from '@/config/env';

interface StripeInlinePaymentProps {
  videoId: string;
  jobId?: string;
  amount: string;
  currency: string;
  onPaymentComplete: (paymentRecordId: string) => void;
  onCancel: () => void;
}

declare global { interface Window { Stripe: any } }

export default function StripeInlinePayment(props: StripeInlinePaymentProps) {
  const { videoId, jobId, amount, currency, onPaymentComplete, onCancel } = props;
  const { callApiWithRecaptcha, isLoaded: recaptchaLoaded } = useApiWithRecaptcha();
  const { triggerRecaptchaLoad } = useRecaptcha();

  // Debug logs
  const debugLog = useCallback((message: string, data?: any) => {
    console.log(`[StripeDebug] ${message}`, data);
  }, []);

  // Debug recaptcha status
  useEffect(() => {
    debugLog('Recaptcha status changed', { recaptchaLoaded });
  }, [recaptchaLoaded]);

  // Auto-load captcha if URL has video_id and status is not 404
  useEffect(() => {
    const checkUrlAndLoadCaptcha = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const videoIdFromUrl = urlParams.get('video_id');
      
      if (videoIdFromUrl && !recaptchaLoaded) {
        debugLog('URL has video_id, checking status and loading captcha...', { videoIdFromUrl });
        
        try {
          // Check if video exists (not 404)
          const response = await fetch(`/api/job/status?video_id=${videoIdFromUrl}`);
          if (response.status !== 404) {
            debugLog('Video exists, auto-loading captcha...');
            await triggerRecaptchaLoad();
          } else {
            debugLog('Video not found (404), not auto-loading captcha');
          }
        } catch (error) {
          debugLog('Error checking video status:', error);
        }
      }
    };

    checkUrlAndLoadCaptcha();
  }, [videoId, recaptchaLoaded, triggerRecaptchaLoad, debugLog]);

  const [stripe, setStripe] = useState<any>(null);
  const [elements, setElements] = useState<any>(null);
  const [clientSecret, setClientSecret] = useState<string>('');

  const [isBooting, setIsBooting] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState('');
  const [success, setSuccess] = useState(false);

  // Optional (chỉ dùng ở confirmPayment, KHÔNG trói vào mount)
  const [email, setEmail] = useState('');
  const [fullname, setFullname] = useState('');

  const paymentElRef = useRef<HTMLDivElement>(null);
  const paymentElementInstanceRef = useRef<any>(null);
  const timeoutRef = useRef<number | null>(null);

  const [elementReady, setElementReady] = useState(false);
  const [elementComplete, setElementComplete] = useState(false);

  const formattedTotal = useMemo(() => {
    const n = Number.parseFloat(amount || '0');
    try { return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(n); }
    catch { return `${currency} ${n.toFixed(2)}`; }
  }, [amount, currency]);

  // 1) Load Stripe.js (one-time) - FIX: thêm mountedRef để tránh remount
  const mountedRef = useRef(false);
  useEffect(() => {
    if (mountedRef.current) return; // Chỉ chạy 1 lần
    mountedRef.current = true;
    
    debugLog('Loading Stripe.js...');
    let pk: string;
    try {
      pk = getStripePublishableKey();
    } catch (error) {
      debugLog('Missing Stripe key');
      setFormError('Missing Stripe publishable key configuration.'); 
      setIsBooting(false); 
      return; 
    }

    const mount = () => {
      try { 
        debugLog('Mounting Stripe...');
        setStripe(window.Stripe(pk)); 
      }
      catch (e: any) { 
        debugLog('Stripe mount error:', e);
        setFormError(e?.message || 'Failed to initialize Stripe.'); 
        setIsBooting(false); 
      }
    };

    if (window.Stripe) {
      debugLog('Stripe already loaded');
      mount();
    } else {
      debugLog('Loading Stripe script...');
      const s = document.createElement('script');
      s.src = 'https://js.stripe.com/v3/';
      s.async = true;
      s.onload = () => {
        debugLog('Stripe script loaded');
        mount();
      };
      s.onerror = () => { 
        debugLog('Stripe script error');
        setFormError('Failed to load Stripe.js. Check network/CSP.'); 
        setIsBooting(false); 
      };
      document.head.appendChild(s);
    }
  }, []); // Empty dependency array - chỉ chạy 1 lần

  // 2) Tạo PaymentIntent — chỉ theo (amount, currency, videoId, jobId)
  const piKeyRef = useRef<string>('');
  const createPaymentIntent = useCallback(async () => {
    debugLog('createPaymentIntent called', { 
      hasStripe: !!stripe, 
      recaptchaLoaded, 
      amount, 
      currency, 
      videoId, 
      jobId 
    });
    
    if (!stripe) {
      debugLog('Missing Stripe');
      return;
    }

    // Only create PaymentIntent if recaptcha is loaded
    if (!recaptchaLoaded) {
      debugLog('Waiting for recaptcha to be ready', { recaptchaLoaded });
      return;
    }
    
    const key = JSON.stringify({ amount, currency, videoId, jobId });
    if (piKeyRef.current === key) {
      debugLog('PaymentIntent already exists, skipping...');
      return; // tránh tạo lại liên tục
    }
    
    debugLog('Creating PaymentIntent...', { amount, currency, videoId, jobId });
    piKeyRef.current = key;

    setIsBooting(true);
    setFormError('');
    
    try {
      const res = await callApiWithRecaptcha('/api/stripe/create-payment-intent', {
        method: 'POST',
        body: JSON.stringify({
          video_id: videoId,
          job_id: jobId,
          amount: Number.parseFloat(amount),
          currency: currency.toLowerCase(),
        }),
      });
      
      if (!res.ok) throw new Error(`Failed to initialize payment. (${res.status})`);
      const data = await res.json();
      debugLog('PaymentIntent created:', data);
      
      if (!data.client_secret) throw new Error('Missing client_secret from server.');

      const { error } = await stripe.retrievePaymentIntent(data.client_secret);
      if (error) throw new Error(`Cannot access PaymentIntent from this publishable key. ${error.message || ''}`);

      setClientSecret(data.client_secret);
      debugLog('Client secret set');
    } catch (e: any) {
      debugLog('PaymentIntent creation error:', e);
      setFormError(e?.message || 'Unable to initialize payment.');
    } finally {
      setIsBooting(false);
    }
  }, [stripe, recaptchaLoaded, amount, currency, videoId, jobId, callApiWithRecaptcha]);

  // FIX: Sử dụng useRef để tránh infinite loop
  const createPaymentIntentRef = useRef(createPaymentIntent);
  createPaymentIntentRef.current = createPaymentIntent;

  useEffect(() => {
    debugLog('createPaymentIntent useEffect triggered', { 
      hasStripe: !!stripe, 
      recaptchaLoaded,
      dependencies: { stripe: !!stripe, recaptchaLoaded, amount, currency, videoId, jobId }
    });
    
    // Chỉ gọi khi có đủ dependencies
    if (stripe && recaptchaLoaded) {
      createPaymentIntentRef.current();
    }
  }, [stripe, recaptchaLoaded]); // Chỉ theo dõi 2 dependencies này

  // 3) Mount Payment Element — chỉ theo (stripe, clientSecret)
  const mountPaymentElement = useCallback(() => {
    if (!stripe || !clientSecret || !paymentElRef.current) return;

    debugLog('Mounting Payment Element...');
    setElementReady(false);
    setElementComplete(false);
    setFormError('');

    // cleanup nếu có
    try { 
      paymentElementInstanceRef.current?.unmount?.(); 
      elements?.destroy?.(); 
    } catch {}

    const appearance = {
      theme: 'flat',
      variables: {
        colorPrimary: '#4f46e5',
        colorBackground: '#ffffff',
        colorText: '#0f172a',
        colorTextSecondary: '#475569',
        colorDanger: '#ef4444',
        borderRadius: '12px',
        spacingUnit: '8px',
        fontFamily: 'ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial',
      },
      rules: {
        '.Label': { fontWeight: '600', fontSize: '13px', color: '#0f172a' },
        '.Input, .Block, .Tab': { border: '1px solid #e2e8f0', boxShadow: 'none' },
        '.Tab--selected': { borderColor: '#4f46e5' },
      },
    };

    const els = stripe.elements({ clientSecret, appearance });
    setElements(els);

    const paymentElement = els.create('payment', {
      layout: 'tabs',
      business: { name: 'DONG HOA AI DATA GENERATION' },
      fields: {
        billingDetails: {
          name: 'auto',
        }}
    });

    // timeout debug
    timeoutRef.current = window.setTimeout(() => {
      debugLog('Payment Element timeout - taking too long');
      setFormError('Stripe is taking too long to load. Check keys/account+mode, PI methods, and CSP allow js.stripe.com/m.stripe.com/hooks.stripe.com.');
    }, 8000);

    // ĐĂNG KÝ TRƯỚC KHI mount
    paymentElement.on('ready', () => {
      debugLog('Payment Element ready');
      if (timeoutRef.current) window.clearTimeout(timeoutRef.current);
      setElementReady(true);
      setFormError('');
    });
    paymentElement.on('change', (e: any) => {
      setElementComplete(!!e.complete);
      if (e.error?.message) setFormError(e.error.message);
    });

    try {
      paymentElement.mount(paymentElRef.current);
      paymentElementInstanceRef.current = paymentElement;
      debugLog('Payment Element mounted successfully');
    } catch (e: any) {
      debugLog('Payment Element mount error:', e);
      setFormError(e?.message || 'Failed to mount Payment Element.');
    }

    return () => {
      if (timeoutRef.current) window.clearTimeout(timeoutRef.current);
      try { paymentElementInstanceRef.current?.unmount?.(); } catch {}
      try { els.destroy?.(); } catch {}
    };
  }, [stripe, clientSecret]);

  // FIX: Sử dụng useRef để tránh infinite loop
  const mountPaymentElementRef = useRef(mountPaymentElement);
  mountPaymentElementRef.current = mountPaymentElement;

  useEffect(() => {
    if (stripe && clientSecret) {
      mountPaymentElementRef.current();
    }
  }, [stripe, clientSecret]); // Chỉ theo dõi 2 dependencies này

  // 4) Submit
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements || !clientSecret) return;
    setIsSubmitting(true);
    setFormError('');

    // Billing details để ở confirmParams - FIX: API backend sẽ không require billing details
    const confirmParams: any = {}; // Để trống, API sẽ xử lý payment mà không cần billing

    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams,
      redirect: 'if_required',
    });

    if (error) {
      setFormError(error.message || 'Payment failed. Please try again.');
      setIsSubmitting(false);
      return;
    }

    if (paymentIntent && ['succeeded', 'processing', 'requires_capture'].includes(paymentIntent.status)) {
      setSuccess(true);
      try {
        const resp = await callApiWithRecaptcha('/api/stripe/capture-payment', {
          method: 'POST',
          body: JSON.stringify({ payment_intent_id: paymentIntent.id, video_id: videoId, job_id: jobId }),
        });
        if (!resp.ok) throw new Error('Capture failed.');
        const data = await resp.json();
        if (!data.payment_record_id) throw new Error('Missing payment_record_id from server.');
        onPaymentComplete(data.payment_record_id);
      } catch (capErr: any) {
        setFormError(capErr?.message || 'Payment succeeded but capture failed. Please contact support.');
        setSuccess(false);
      }
    } else {
      setFormError('Payment not completed yet. Please try again.');
    }

    setIsSubmitting(false);
  };

  // Skeleton
  if (isBooting) {
    return (
        <div className="w-full overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
          <div className="bg-gradient-to-r from-indigo-600 to-blue-600 px-6 py-6 text-white">
            <div className="h-5 w-40 animate-pulse rounded bg-white/25" />
            <div className="mt-3 h-8 w-56 animate-pulse rounded bg-white/25" />
          </div>
          <div className="space-y-4 p-6">
            <div className="h-11 w-full animate-pulse rounded-xl bg-slate-100" />
            <div className="h-24 w-full animate-pulse rounded-xl bg-slate-100" />
            <div className="h-11 w-full animate-pulse rounded-xl bg-slate-100" />
          </div>
        </div>
    );
  }

  if (success) {
    return (
        <div className="w-full rounded-3xl border border-emerald-200 bg-gradient-to-br from-emerald-50 to-green-50 p-8 text-center shadow-sm">
          <div className="mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-emerald-500">
            <CheckCircle className="h-10 w-10 text-white" />
          </div>
          <h3 className="text-2xl font-bold text-emerald-800">Payment Successful</h3>
          <p className="mt-1 text-emerald-700">Your video is being processed securely.</p>
        </div>
    );
  }
  return (
      <div className="w-[100%] md:w-full md:ml-0 md:mr-0 overflow-hidden md:rounded-3xl md:border md:border-slate-200 bg-white shadow-sm">
        {/* Header */}
        <div className="relative md:rounded-t-3xl bg-gradient-to-r from-indigo-600 to-blue-600 px-6 py-6 text-white" onClick={triggerRecaptchaLoad}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="grid h-10 w-10 place-items-center rounded-full bg-white/10">
                <ShieldCheck className="h-6 w-6" />
              </div>
              <div>
                <p className="text-xs/5 opacity-90">Total Amount</p>
                <h2 className="text-2xl font-bold">{formattedTotal}</h2>
              </div>
            </div>
            <div className="hidden sm:flex items-center gap-2">
              <span className="rounded-full bg-white/15 px-3 py-1 text-xs font-medium">SSL</span>
              <span className="rounded-full bg-white/15 px-3 py-1 text-xs font-medium">PCI</span>
            </div>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} 
              className="space-y-6 md:p-2 md:p-6" 
              onClick={triggerRecaptchaLoad}
              onMouseDown={triggerRecaptchaLoad}
              onTouchStart={triggerRecaptchaLoad}
              onFocus={triggerRecaptchaLoad}>
          {/* Payment */}
          <div>
            <div className="mb-3 flex items-center gap-2 ml-4 mr-4 mt-4 md:ml-0 md:mr-0 md:mt-0">
              <span className="grid h-5 w-5 place-items-center rounded-full bg-indigo-600 text-[11px] font-bold text-white">1</span>
              <h3 className="text-sm font-semibold text-slate-900">Payment Method</h3>
            </div>

            <div className="rounded-xl md:border md:border-slate-200 p-3" 
                 onClick={triggerRecaptchaLoad}
                 onMouseDown={triggerRecaptchaLoad}
                 onTouchStart={triggerRecaptchaLoad}>
              <div id="payment-element" 
                   ref={paymentElRef} 
                   style={{ minHeight: 56 }}
                   onClick={triggerRecaptchaLoad}
                   onMouseDown={triggerRecaptchaLoad} />
              {!elementReady && <div className="mt-3 h-9 w-full animate-pulse rounded-lg bg-slate-100" />}
            </div>
          </div>

          {/* Error */}
          <div aria-live="polite">
            {formError && (
                <Alert variant="destructive" className="rounded-xl border-red-200 bg-red-50">
                  <AlertCircle className="h-5 w-5 text-red-600" />
                  <AlertDescription className="text-red-700">{formError}</AlertDescription>
                </Alert>
            )}
          </div>

          {/* Debug: Manual trigger button */}
          {!recaptchaLoaded && (
            <div className="rounded-lg border border-orange-200 bg-orange-50 p-3">
              <p className="text-sm text-orange-800 mb-2">
                Click here to load security verification:
              </p>
              <Button 
                type="button" 
                variant="outline" 
                size="sm"
                onClick={triggerRecaptchaLoad}
                className="text-orange-700 border-orange-300 hover:bg-orange-100">
                Load Security Verification
              </Button>
            </div>
          )}

          {/* Actions */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 ml-4 mr-4">
            <Button type="button" variant="outline" onClick={onCancel} disabled={isSubmitting}
                    className="h-11 rounded-xl border-slate-200 text-slate-700 hover:bg-slate-50">
              Cancel
            </Button>

            <Button type="submit" disabled={isSubmitting || !elementComplete}
                    className="h-11 rounded-xl bg-gradient-to-r from-indigo-600 to-blue-600 font-semibold text-white shadow-lg transition-all hover:from-indigo-700 hover:to-blue-700 disabled:opacity-60">
              {isSubmitting ? (
                  <div className="flex items-center gap-2">
                    <div className="h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent" />
                    Processing…
                  </div>
              ) : (
                  <div className="flex items-center gap-2">
                    <CreditCard className="h-5 w-5" />
                    Pay {formattedTotal}
                  </div>
              )}
            </Button>
          </div>

          {/* Trust footer */}
          <div className="mt-1 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 pb-1 text-xs text-slate-500">
            <div className="flex items-center gap-1"><span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> PCI DSS Compliant</div>
            <div className="flex items-center gap-1"><span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> 256-bit SSL Encryption</div>
            <div className="flex items-center gap-1"><span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> Bank-Level Security</div>
          </div>
        </form>
      </div>
  );
}
